import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../auth';
import { authHeaders, jsonAuthHeaders } from '../../../shared/authHeaders';
import { formatShortDateTime } from '../../../shared/dates';

type TestArtifact = {
  id: string;
  name: string;
  sizeBytes: number;
  expired: boolean;
  createdAt: string;
  expiresAt: string;
  githubDownloadUrl: string;
  adminDownloadUrl: string;
};

type TestStep = {
  name: string;
  status: string;
  conclusion: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationSeconds: number | null;
};

type TestSuite = {
  key: string;
  label: string;
  command: string;
  criteria: string[];
  status: string;
  conclusion: string | null;
  jobUrl: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationSeconds: number | null;
  steps: TestStep[];
  artifacts: TestArtifact[];
};

export type ScreenshotCheckResult = {
  label: string;
  status: 'passed' | 'failed' | 'unverified';
  error: string | null;
};

type TestReviewScreenshot = {
  kind?: 'screenshot';
  label?: string;
  fileName: string;
  path: string;
  contentType: string;
  sizeBytes: number;
  dataUrl: string | null;
  omittedReason: string | null;
  annotated?: boolean;
  stepIndex?: number | null;
  caption?: string | null;
  checks?: ScreenshotCheckResult[];
  pageUrl?: string | null;
  capturedAt?: string | null;
  capture?: 'full-page' | 'viewport' | 'element' | null;
  viewportOnlyReason?: string | null;
  cropWarning?: string | null;
};

type TestReviewMedia = TestReviewScreenshot | {
  kind: 'video';
  label?: string;
  fileName: string;
  path: string;
  contentType: string;
  sizeBytes: number;
  dataUrl: string | null;
  omittedReason: string | null;
};

type TestReviewFlowStep = {
  index: number;
  title: string;
  verify: string;
  checks?: ScreenshotCheckResult[];
  route: string | null;
  expectedRoute: string | null;
  status: 'passed' | 'failed' | 'not-reached';
  error: string | null;
  durationMs: number | null;
  screenshots: string[];
  startedAt: string | null;
};

type TestReviewFlow = {
  schemaVersion: number;
  kind: 'flow';
  id: string;
  role: string;
  goal: string;
  persona: string | null;
  status: 'passed' | 'failed';
  failedStepIndex: number | null;
  stepCount: number;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  steps: TestReviewFlowStep[];
};

type TestReviewEvidence = {
  key: string;
  testId?: string;
  file: string;
  title: string;
  status: string;
  outcome: string;
  durationMs: number | null;
  startedAt?: string | null;
  error: string | null;
  description: string;
  criteria: string[];
  flow?: TestReviewFlow | null;
  media?: TestReviewMedia[];
  screenshots?: TestReviewScreenshot[];
  screenshot: TestReviewScreenshot | null;
  screenshotIssues?: string[];
  screenshotUnavailableReason: string | null;
  video?: TestReviewMedia | null;
  videoUnavailableReason?: string | null;
};

type TestReviewGap = {
  id: string;
  severity: string;
  title: string;
  currentBehavior: string;
  recommendedTests: string[];
};

type TestReviewJourney = {
  id: string;
  label: string;
  role: string;
  coverageStatus: string;
  resultStatus: string;
  entryPoints: string[];
  routes: string[];
  goal: string;
  criticalSteps: string[];
  expectedOutcome: string;
  reviewCriteria: string[];
  tests: TestReviewEvidence[];
  gaps: TestReviewGap[];
};

type TestReviewReport = {
  schemaVersion: number;
  generatedAt: string;
  matching: {
    keyFormat: string;
    rule: string;
    screenshotRule: string;
  };
  source: {
    playwrightJsonPath: string;
    hasPlaywrightResults: boolean;
    playwrightError: string | null;
    playwrightStartedAt?: string | null;
    playwrightDurationMs?: number | null;
    runBlocked?: boolean;
    runBlockedReason?: string | null;
  };
  summary: {
    journeyCount: number;
    coveredJourneyCount: number;
    partialJourneyCount: number;
    gapJourneyCount: number;
    failedJourneyCount: number;
    blockedJourneyCount?: number;
    notRunJourneyCount: number;
    testCount: number;
    passedTestCount: number;
    failedTestCount: number;
    blockedTestCount?: number;
    notRunTestCount: number;
    screenshotCount: number;
    annotatedScreenshotCount?: number;
    screenshotCheckCount?: number;
    failedScreenshotCheckCount?: number;
    cropWarningCount?: number;
    testsWithScreenshotIssues?: number;
    videoCount?: number;
    embeddedVideoCount?: number;
    flowTestCount?: number;
    failedFlowStepCount?: number;
    unmappedTestCount: number;
  };
  journeys: TestReviewJourney[];
  unmappedTests: TestReviewEvidence[];
  gaps: TestReviewGap[];
};

type TestStatus = {
  configured: boolean;
  error?: string;
  tokenRequired?: boolean;
  connectionHint?: 'missing-token' | 'not-found' | 'forbidden' | 'unauthorized' | 'unknown';
  configurationHelp?: string | null;
  repository: string;
  workflowId: string;
  generatedAt: string;
  schedule: string;
  latestRun: null | {
    id: number;
    name: string | null;
    number: number;
    attempt: number;
    status: string;
    conclusion: string | null;
    event: string;
    branch: string | null;
    sha: string;
    shortSha: string;
    actor: string | null;
    message: string | null;
    url: string;
    createdAt: string;
    startedAt: string | null;
    updatedAt: string;
    durationSeconds: number | null;
  };
  testReview: TestReviewReport | null;
  testReviewArtifact: TestArtifact | null;
  testReviewSource?: {
    kind: 'github-artifact' | 'local-file';
    label: string;
    path?: string;
    artifactId?: string;
    modifiedAt?: string;
    sizeBytes?: number;
  } | null;
  testReviewError: string | null;
  suiteDetailError: string | null;
  suites: TestSuite[];
  artifacts: TestArtifact[];
};

type ReviewFilter = 'all' | 'failed' | 'needs-attention' | 'missing-screenshots' | 'screenshot-issues';

type ReviewEvidenceItem = {
  journey: TestReviewJourney | null;
  test: TestReviewEvidence;
};

type JourneyRoleGroup = {
  role: string;
  entries: { journey: TestReviewJourney; tests: TestReviewEvidence[] }[];
};

type TestChangePlanDraft = {
  title: string;
  summary: string;
  body: string;
  planType: 'external_harness';
};

type TestChangePlanNotice = {
  message: string;
  planId: string;
  planStatus: 'draft';
};

type LightboxEntry = {
  media: TestReviewMedia;
  title: string;
};

type LightboxItem = {
  entries: LightboxEntry[];
  index: number;
  test: TestReviewEvidence;
};

type DisplayRunSummary = {
  eyebrow: string;
  title: string;
  message: string;
  status: string;
  conclusion: string | null;
  startedLabel: string;
  startedAt: string | null;
  durationLabel: string;
  durationText: string;
  trigger: string;
  branch: string;
  url: string | null;
};

export function normalizeReviewPageUrl(value: string | null | undefined) {
  if (!value) return null;

  try {
    const url = new URL(value);
    const isLocalApp = ['localhost', '127.0.0.1', '::1'].includes(url.hostname);
    if (!isLocalApp) return value;

    url.protocol = 'http:';
    url.hostname = 'localhost';
    url.port = '5273';
    return url.toString();
  } catch {
    return value;
  }
}

export function formatDuration(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

export function formatMilliseconds(milliseconds: number | null | undefined) {
  if (milliseconds === null || milliseconds === undefined) return '-';
  if (milliseconds < 1000) return `${milliseconds}ms`;
  return formatDuration(Math.round(milliseconds / 1000));
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function statusLabel(status: string, conclusion: string | null) {
  if (status !== 'completed') return status;
  return conclusion || status;
}

export function statusClass(status: string, conclusion: string | null) {
  if (status !== 'completed') return 'border-warn/25 bg-warn/10 text-warn';
  switch (conclusion) {
    case 'success':
      return 'border-ok/25 bg-ok/10 text-ok';
    case 'cancelled':
    case 'skipped':
      return 'border-line bg-panel text-text-dim';
    default:
      return 'border-red-500/25 bg-red-500/10 text-red-600';
  }
}

export function reviewStatusClass(status: string) {
  switch (status) {
    case 'passed':
      return 'border-ok/25 bg-ok/10 text-ok';
    case 'flaky':
      return 'border-warn/25 bg-warn/10 text-warn';
    case 'failed':
    case 'timedOut':
    case 'interrupted':
      return 'border-red-500/25 bg-red-500/10 text-red-600';
    case 'run-blocked':
      return 'border-blue-500/25 bg-blue-500/10 text-blue-700';
    case 'gap':
    case 'not-run':
    case 'no-tests':
      return 'border-warn/25 bg-warn/10 text-warn';
    default:
      return 'border-line bg-panel text-text-dim';
  }
}

export function reviewStatusLabel(status: string) {
  return status.replace(/-/g, ' ');
}

export function isFailedReviewStatus(status: string) {
  return status === 'failed' || status === 'timedOut' || status === 'interrupted';
}

export function isNeedsAttentionReviewStatus(status: string) {
  return isFailedReviewStatus(status)
    || status === 'flaky'
    || status === 'run-blocked'
    || status === 'not-run'
    || status === 'gap'
    || status === 'no-tests'
    || status === 'unknown';
}

export function flowSummaryLabel(flow: Pick<TestReviewFlow, 'status' | 'stepCount' | 'failedStepIndex'>) {
  if (flow.status === 'passed') {
    return `Flow passed · ${flow.stepCount} step${flow.stepCount === 1 ? '' : 's'}`;
  }
  return `Failed at step ${flow.failedStepIndex ?? '?'} of ${flow.stepCount}`;
}

export function flowSummaryClass(flow: Pick<TestReviewFlow, 'status'>) {
  return flow.status === 'passed'
    ? 'border-ok/25 bg-ok/10 text-ok'
    : 'border-red-500/25 bg-red-500/10 text-red-600';
}

export function flowStepStatusClass(status: TestReviewFlowStep['status']) {
  switch (status) {
    case 'passed':
      return 'border-ok/25 bg-ok/10 text-ok';
    case 'failed':
      return 'border-red-500/25 bg-red-500/10 text-red-600';
    default:
      return 'border-line bg-panel text-text-faint';
  }
}

export function flowStepStatusLabel(status: TestReviewFlowStep['status']) {
  return status === 'not-reached' ? 'not reached' : status;
}

export function checksSummary(checks: ScreenshotCheckResult[]) {
  const passed = checks.filter(check => check.status === 'passed').length;
  const failed = checks.filter(check => check.status === 'failed').length;
  const unverified = checks.filter(check => check.status === 'unverified').length;
  return [
    passed ? `${passed} passed` : null,
    failed ? `${failed} failed` : null,
    unverified ? `${unverified} unverified` : null,
  ].filter(Boolean).join(' · ');
}

function CheckList({ checks, tone = 'light' }: { checks: ScreenshotCheckResult[]; tone?: 'light' | 'dark' }) {
  const labelClass = tone === 'dark' ? 'text-white/80' : 'text-text-dim';
  const mutedClass = tone === 'dark' ? 'text-white/45' : 'text-text-faint';
  const passClass = tone === 'dark' ? 'text-emerald-400' : 'text-ok';
  const failIconClass = tone === 'dark' ? 'text-red-400' : 'text-red-600';
  const failLabelClass = tone === 'dark' ? 'text-red-300' : 'text-red-700';
  const failErrorClass = tone === 'dark' ? 'text-red-300/80' : 'text-red-600/90';

  return (
    <ul className="space-y-1 text-xs leading-4">
      {checks.map((check, index) => (
        <li key={`${check.label}-${index}`}>
          <div className="flex gap-1.5">
            <span
              aria-hidden="true"
              className={`shrink-0 font-semibold ${
                check.status === 'passed' ? passClass : check.status === 'failed' ? failIconClass : mutedClass
              }`}
            >
              {check.status === 'passed' ? '✓' : check.status === 'failed' ? '✗' : '•'}
            </span>
            <span className={`min-w-0 ${check.status === 'failed' ? `font-semibold ${failLabelClass}` : labelClass}`}>
              {check.label}
              {check.status === 'unverified' && (
                <span className={`ml-1 text-[10px] uppercase tracking-wide ${mutedClass}`}>unverified</span>
              )}
            </span>
          </div>
          {check.status === 'failed' && check.error && (
            <div className={`pl-4 text-[11px] leading-4 ${failErrorClass}`}>{check.error}</div>
          )}
        </li>
      ))}
    </ul>
  );
}

export const JOURNEY_ROLE_ORDER = ['anonymous', 'organizer', 'model', 'artist', 'admin'];

export function journeyRoleSortIndex(role: string) {
  const index = JOURNEY_ROLE_ORDER.indexOf(role);
  return index === -1 ? JOURNEY_ROLE_ORDER.length : index;
}

export function journeyMatchesQuery(
  journey: Pick<TestReviewJourney, 'label' | 'id' | 'role' | 'goal' | 'entryPoints' | 'routes'> & {
    tests?: Pick<TestReviewEvidence, 'title' | 'file' | 'flow'>[];
  },
  query: string,
) {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystacks = [
    journey.label,
    journey.id,
    journey.role,
    journey.goal,
    ...journey.entryPoints,
    ...journey.routes,
    ...(journey.tests ?? []).flatMap(test => [
      test.title ?? '',
      test.file ?? '',
      test.flow?.id ?? '',
      test.flow?.goal ?? '',
    ]),
  ];
  return haystacks.some(value => value.toLowerCase().includes(needle));
}

const JOURNEY_RESULT_ROLLUP_ORDER = ['failed', 'flaky', 'passed', 'run-blocked', 'not-run', 'gap', 'no-tests'];

export function journeyResultRollup(journeys: Pick<TestReviewJourney, 'resultStatus'>[]) {
  const counts = new Map<string, number>();
  for (const journey of journeys) {
    counts.set(journey.resultStatus, (counts.get(journey.resultStatus) ?? 0) + 1);
  }
  const known = JOURNEY_RESULT_ROLLUP_ORDER.filter(status => counts.has(status));
  const extras = Array.from(counts.keys()).filter(status => !JOURNEY_RESULT_ROLLUP_ORDER.includes(status)).sort();
  return [...known, ...extras]
    .map(status => {
      const count = counts.get(status) ?? 0;
      const label = status === 'gap' ? `gap${count === 1 ? '' : 's'}` : reviewStatusLabel(status);
      return `${count} ${label}`;
    })
    .join(' · ');
}

function getJourneyFlow(journey: TestReviewJourney): TestReviewFlow | null {
  const flows = journey.tests
    .map(test => test.flow)
    .filter((flow): flow is TestReviewFlow => Boolean(flow));
  return flows.find(flow => flow.status === 'failed') ?? flows[0] ?? null;
}

export function latestRunSummary(status: TestStatus | null) {
  if (!status?.latestRun) return null;
  return status.latestRun;
}

export function displayRunSummary(status: TestStatus | null): DisplayRunSummary | null {
  const localSource = status?.testReviewSource?.kind === 'local-file'
    ? status.testReviewSource
    : null;

  if (status?.testReview && localSource) {
    const summary = status.testReview.summary;
    const failedCount = summary.failedTestCount + (summary.blockedTestCount ?? 0) + summary.notRunTestCount;
    const conclusion = failedCount > 0 ? 'failure' : 'success';
    const parts = [
      `${summary.passedTestCount}/${summary.testCount} tests passed`,
      `${summary.screenshotCount} screenshots`,
      `${summary.videoCount ?? 0} videos`,
    ];

    return {
      eyebrow: 'Latest local report',
      title: 'Local test report',
      message: parts.join(' · '),
      status: 'completed',
      conclusion,
      startedLabel: 'Generated',
      startedAt: localSource.modifiedAt ?? status.testReview.generatedAt,
      durationLabel: 'Duration',
      durationText: formatMilliseconds(status.testReview.source.playwrightDurationMs),
      trigger: 'local',
      branch: '-',
      url: null,
    };
  }

  const run = latestRunSummary(status);
  if (!run) return null;

  return {
    eyebrow: 'Latest GitHub run',
    title: `#${run.number} · ${run.shortSha}`,
    message: run.message || 'No commit message',
    status: run.status,
    conclusion: run.conclusion,
    startedLabel: 'Started',
    startedAt: run.startedAt || run.createdAt,
    durationLabel: 'Duration',
    durationText: formatDuration(run.durationSeconds),
    trigger: run.event,
    branch: run.branch || '-',
    url: run.url,
  };
}

export function TestStatusPage() {
  const { getToken } = useAuth();
  const [status, setStatus] = useState<TestStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const [journeyQuery, setJourneyQuery] = useState('');
  const [expandedJourneyId, setExpandedJourneyId] = useState<string | null>(null);
  const [isEvidenceBrowserOpen, setIsEvidenceBrowserOpen] = useState(false);
  const [lightboxItem, setLightboxItem] = useState<LightboxItem | null>(null);
  const [selectedTestItem, setSelectedTestItem] = useState<ReviewEvidenceItem | null>(null);
  const [testChangeRequest, setTestChangeRequest] = useState('');
  const [isCreatingTestChangePlan, setIsCreatingTestChangePlan] = useState(false);
  const [testChangePlanNotice, setTestChangePlanNotice] = useState<TestChangePlanNotice | null>(null);
  const [testChangePlanError, setTestChangePlanError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch('/api/admin/test-status', {
        headers: authHeaders(token),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load test status');
      }
      setStatus(data);
    } catch (err) {
      console.error('Error loading test status:', err);
      setError(err instanceof Error ? err.message : 'Failed to load test status');
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);

  async function downloadArtifact(artifact: TestArtifact) {
    setDownloadError(null);
    try {
      const token = await getToken();
      const response = await fetch(artifact.adminDownloadUrl, {
        headers: authHeaders(token),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to download artifact');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${artifact.name}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading artifact:', err);
      setDownloadError(err instanceof Error ? err.message : 'Failed to download artifact');
    }
  }

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const run = displayRunSummary(status);
  const failedSuites = useMemo(
    () => status?.suites.filter(suite => suite.conclusion && suite.conclusion !== 'success') ?? [],
    [status]
  );
  const review = status?.testReview ?? null;
  const isUsingLocalReview = Boolean(review && status?.testReviewSource?.kind === 'local-file');
  const allReviewItems = useMemo<ReviewEvidenceItem[]>(() => {
    if (!review) return [];
    return [
      ...review.journeys.flatMap(journey => journey.tests.map(test => ({ journey, test }))),
      ...review.unmappedTests.map(test => ({ journey: null, test })),
    ];
  }, [review]);
  const filteredReviewItems = useMemo(() => (
    allReviewItems.filter(({ test }) => matchesReviewFilter(test, reviewFilter))
  ), [allReviewItems, reviewFilter]);
  const reviewFilterOptions = useMemo(() => ([
    { key: 'all' as const, label: 'All', count: allReviewItems.length },
    { key: 'failed' as const, label: 'Failed', count: allReviewItems.filter(({ test }) => matchesReviewFilter(test, 'failed')).length },
    { key: 'needs-attention' as const, label: 'Needs attention', count: allReviewItems.filter(({ test }) => matchesReviewFilter(test, 'needs-attention')).length },
    { key: 'missing-screenshots' as const, label: 'Missing screenshots', count: allReviewItems.filter(({ test }) => matchesReviewFilter(test, 'missing-screenshots')).length },
    { key: 'screenshot-issues' as const, label: 'Screenshot issues', count: allReviewItems.filter(({ test }) => matchesReviewFilter(test, 'screenshot-issues')).length },
  ]), [allReviewItems]);
  const journeyRoleGroups = useMemo<JourneyRoleGroup[]>(() => {
    const journeys = review?.journeys ?? [];
    const visible = journeys
      .filter(journey => journeyMatchesQuery(journey, journeyQuery))
      .map(journey => ({
        journey,
        tests: journey.tests.filter(test => matchesReviewFilter(test, reviewFilter)),
      }))
      .filter(({ tests }) => reviewFilter === 'all' || tests.length > 0);

    const groups = new Map<string, JourneyRoleGroup['entries']>();
    for (const entry of visible) {
      const list = groups.get(entry.journey.role) ?? [];
      list.push(entry);
      groups.set(entry.journey.role, list);
    }
    return Array.from(groups.entries())
      .map(([role, entries]) => ({ role, entries }))
      .sort((a, b) => (
        journeyRoleSortIndex(a.role) - journeyRoleSortIndex(b.role) || a.role.localeCompare(b.role)
      ));
  }, [review, journeyQuery, reviewFilter]);
  const filteredUnmappedTests = useMemo(() => {
    if (!review) return [];
    return review.unmappedTests.filter(test => matchesReviewFilter(test, reviewFilter));
  }, [review, reviewFilter]);
  const failureReasonItems = useMemo(() => (
    filteredReviewItems.filter(({ test }) => isFailedReviewStatus(test.status))
  ), [filteredReviewItems]);
  const evidenceItems = filteredReviewItems;
  const openLightbox = useCallback((media: TestReviewMedia, test: TestReviewEvidence, title?: string) => {
    setLightboxItem(buildLightboxItem(media, test, title));
  }, []);
  const openTestSummary = useCallback((item: ReviewEvidenceItem) => {
    setSelectedTestItem(item);
    setTestChangeRequest('');
    setTestChangePlanNotice(null);
    setTestChangePlanError(null);
  }, []);

  async function createTestChangePlan() {
    if (!selectedTestItem || !testChangeRequest.trim()) return;

    setIsCreatingTestChangePlan(true);
    setTestChangePlanError(null);
    setTestChangePlanNotice(null);
    try {
      const token = await getToken();
      const planDraft = buildTestChangePlanDraft({
        item: selectedTestItem,
        request: testChangeRequest,
      });
      const response = await fetch('/api/admin/plans', {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify(planDraft),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create test change plan');
      }

      setTestChangePlanNotice({
        message: 'Draft plan created.',
        planId: data.plan.id,
        planStatus: 'draft',
      });
      setTestChangeRequest('');
    } catch (err) {
      console.error('Error creating test change plan:', err);
      setTestChangePlanError(err instanceof Error ? err.message : 'Failed to create test change plan');
    } finally {
      setIsCreatingTestChangePlan(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-sm text-text-dim">Loading test status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-2xl">
        <h1 className="text-3xl font-serif text-text mb-3">Test Status</h1>
        <div className="border border-red-500/20 bg-red-500/10 text-red-700 rounded-lg p-4 text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif text-text mb-2">Test Status</h1>
          <p className="text-sm text-text-dim max-w-2xl">
            GitHub Actions runs this suite nightly and on every push to main. Playwright artifacts contain the browser
            report, traces, videos, and screenshots when tests capture them. On localhost, this page uses the latest
            local review report when one is available.
          </p>
        </div>
        <button
          type="button"
          onClick={loadStatus}
          className="px-4 py-2 border border-line rounded-lg text-sm font-semibold text-text hover:bg-panel2 transition-colors"
        >
          Refresh
        </button>
      </div>

      <section className="grid gap-4 lg:grid-cols-[1.4fr_1fr] mb-8">
        <div className="border border-line rounded-lg bg-panel p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-1">{run?.eyebrow ?? 'Latest run'}</div>
              {run ? (
                <>
                  <div className="text-xl font-serif text-text">
                    {run.title}
                  </div>
                  <div className="text-sm text-text-dim mt-1">
                    {run.message}
                  </div>
                </>
              ) : (
                <div className="text-sm text-text-dim">No workflow run or local report found.</div>
              )}
            </div>
            {run && (
              <span className={`px-2.5 py-1 rounded-full border text-xs font-semibold ${statusClass(run.status, run.conclusion)}`}>
                {statusLabel(run.status, run.conclusion)}
              </span>
            )}
          </div>

          {run && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold">{run.startedLabel}</div>
                <div className="text-text">{formatShortDateTime(run.startedAt)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold">{run.durationLabel}</div>
                <div className="text-text">{run.durationText}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold">Trigger</div>
                <div className="text-text">{run.trigger}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold">Branch</div>
                <div className="text-text">{run.branch || '-'}</div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            {run?.url && (
              <a
                href={run.url}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 bg-text text-bg rounded-lg text-sm font-semibold"
              >
                Open in GitHub
              </a>
            )}
          </div>
        </div>

        <div className="border border-line rounded-lg bg-panel p-5">
          <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-3">Schedule</div>
          <p className="text-sm text-text">{status?.schedule}</p>
          <div className="mt-5 text-sm text-text-dim">
            <div>Repository: {status?.repository}</div>
            <div>Workflow: {status?.workflowId}</div>
            <div>Refreshed: {formatShortDateTime(status?.generatedAt)}</div>
          </div>
          {failedSuites.length > 0 && (
            <div className="mt-5 border border-red-500/20 bg-red-500/10 rounded-lg p-3 text-sm text-red-700">
              {failedSuites.length} suite{failedSuites.length === 1 ? '' : 's'} need attention.
            </div>
          )}
          {status?.suiteDetailError && (
            <div className="mt-5 border border-warn/25 bg-warn/10 rounded-lg p-3 text-sm text-text">
              <div className="font-semibold">Some GitHub Actions details could not be loaded.</div>
              <div className="text-text-dim mt-1">{status.suiteDetailError}</div>
            </div>
          )}
        </div>
      </section>

      {status && !status.configured && (
        <div className="mb-8 border border-warn/25 bg-warn/10 rounded-lg p-4 text-sm text-text">
          <div className="font-semibold mb-1">GitHub Actions status is not connected yet.</div>
          <p className="text-text-dim">
            {status.error || 'The API could not read the workflow status.'}
          </p>
          {(status.configurationHelp || status.tokenRequired) && (
            <p className="mt-2 text-text-dim">
              {status.configurationHelp
                || 'Set GITHUB_ACTIONS_TOKEN, GITHUB_TOKEN, or GH_TOKEN with read access to Actions for this private repository, then restart the API server.'}
            </p>
          )}
        </div>
      )}

      {downloadError && (
        <div className="mb-8 border border-red-500/20 bg-red-500/10 rounded-lg p-4 text-sm text-red-700">
          {downloadError}
        </div>
      )}

      <section className="mb-10">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-serif text-text">Journey Review</h2>
            <p className="text-sm text-text-dim mt-1 max-w-3xl">
              Each journey explains the user goal, the criteria we expect to remain true, the tests that prove it, and
              screenshots from browser tests when CI captured them.
            </p>
          </div>
          {review && (
            <div className="flex flex-wrap gap-2 md:justify-end">
              {reviewFilterOptions.map(option => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setReviewFilter(option.key)}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-semibold transition-colors ${
                    reviewFilter === option.key
                      ? 'bg-text text-bg border-text'
                      : 'border-line text-text-dim hover:text-text hover:bg-panel2'
                  }`}
                >
                  {option.label} <span className="opacity-70">{option.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {!review && (
          <div className="border border-line rounded-lg bg-panel p-5 text-sm text-text-dim">
            <div className="font-semibold text-text mb-1">No journey review report is available yet.</div>
            <p>
              {status?.testReviewError
                ? status.testReviewError
                : 'The next GitHub Actions run will upload the test-review-report artifact for this page to display.'}
            </p>
          </div>
        )}

        {review && (
          <>
            <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7 mb-4">
              <ReviewMetric
                label="Journeys"
                value={review.summary.blockedJourneyCount
                  ? `${review.summary.blockedJourneyCount} blocked / ${review.summary.journeyCount}`
                  : review.summary.journeyCount}
              />
              <ReviewMetric
                label="Tests"
                value={review.summary.blockedTestCount
                  ? `${review.summary.blockedTestCount} blocked / ${review.summary.testCount}`
                  : review.summary.testCount}
              />
              <ReviewMetric label="Screenshots" value={review.summary.screenshotCount} />
              <ReviewMetric label="Annotated shots" value={review.summary.annotatedScreenshotCount ?? 0} />
              <ReviewMetric label="Crop warnings" value={review.summary.cropWarningCount ?? 0} />
              <ReviewMetric
                label="Videos"
                value={review.summary.videoCount
                  ? `${review.summary.embeddedVideoCount ?? 0} playable / ${review.summary.videoCount}`
                  : 0}
              />
              <ReviewMetric label="Backlog gaps" value={review.summary.gapJourneyCount} />
            </div>

            <div className="border border-line rounded-lg bg-panel p-4 mb-4 text-sm text-text-dim">
              <div className="font-semibold text-text mb-1">How this is matched</div>
              <p>{review.matching.rule}</p>
              <p className="mt-1">{review.matching.screenshotRule}</p>
              {review.source.runBlocked && (
                <div className="mt-3 rounded-lg border border-blue-500/25 bg-blue-500/10 p-3 text-blue-800">
                  <div className="font-semibold">Browser journey run was blocked before evidence could be captured.</div>
                  <p className="mt-1 text-blue-800/80">
                    {review.source.runBlockedReason || review.source.playwrightError || 'Playwright did not produce a JSON report.'}
                  </p>
                </div>
              )}
              {!review.source.runBlocked && !review.source.hasPlaywrightResults && review.source.playwrightError && (
                <p className="mt-2 text-warn">{review.source.playwrightError}</p>
              )}
              {status?.testReviewArtifact && (
                <div className="mt-2">
                  Artifact: {status.testReviewArtifact.name} · {formatBytes(status.testReviewArtifact.sizeBytes)}
                </div>
              )}
              {!status?.testReviewArtifact && status?.testReviewSource && (
                <div className="mt-2">
                  {status.testReviewSource.label}
                  {status.testReviewSource.modifiedAt ? ` · ${formatShortDateTime(status.testReviewSource.modifiedAt)}` : ''}
                  {status.testReviewSource.sizeBytes ? ` · ${formatBytes(status.testReviewSource.sizeBytes)}` : ''}
                </div>
              )}
            </div>

            {failureReasonItems.length > 0 && (
              <FailureReasonsPanel
                items={failureReasonItems}
                onOpenTest={openTestSummary}
              />
            )}

            <div className="mb-3">
              <input
                type="search"
                value={journeyQuery}
                onChange={(event) => setJourneyQuery(event.target.value)}
                placeholder="Find a journey — name, role, route, or goal…"
                aria-label="Find a journey by name, role, route, or goal"
                className="input w-full text-sm"
              />
            </div>

            {journeyRoleGroups.length > 0 && (
              <nav
                aria-label="Jump to a role's journeys"
                className="sticky top-0 z-10 -mx-2 mb-4 flex flex-wrap gap-2 bg-bg/95 px-2 py-2 backdrop-blur"
              >
                {journeyRoleGroups.map(group => (
                  <a
                    key={group.role}
                    href={`#journey-role-${group.role}`}
                    className="px-3 py-1.5 rounded-lg border border-line text-sm font-semibold capitalize text-text-dim transition-colors hover:bg-panel2 hover:text-text"
                  >
                    {group.role} <span className="opacity-70">{group.entries.length}</span>
                  </a>
                ))}
              </nav>
            )}

            <div className="space-y-8">
              {journeyRoleGroups.map(group => (
                <section key={group.role} id={`journey-role-${group.role}`} className="scroll-mt-16">
                  <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="text-xl font-serif text-text capitalize">{group.role}</h3>
                    <span className="text-sm text-text-dim">
                      {group.entries.length} journey{group.entries.length === 1 ? '' : 's'}
                    </span>
                    <span className="text-sm text-text-faint">
                      {journeyResultRollup(group.entries.map(entry => entry.journey))}
                    </span>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {group.entries.map(({ journey, tests }) => (
                      expandedJourneyId === journey.id ? (
                        <div key={journey.id} className="md:col-span-2">
                          <JourneyDetailCard
                            journey={journey}
                            tests={tests}
                            isFiltered={reviewFilter !== 'all'}
                            onOpenMedia={openLightbox}
                            onOpenTest={(test) => openTestSummary({ journey, test })}
                            onCollapse={() => setExpandedJourneyId(null)}
                          />
                        </div>
                      ) : (
                        <JourneyIndexCard
                          key={journey.id}
                          journey={journey}
                          tests={tests}
                          isFiltered={reviewFilter !== 'all'}
                          onExpand={() => setExpandedJourneyId(journey.id)}
                        />
                      )
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {journeyRoleGroups.length === 0 && filteredUnmappedTests.length === 0 && (
              <div className="mt-4 border border-line rounded-lg bg-panel p-5 text-sm text-text-dim">
                No journeys match the current search or filters.
              </div>
            )}

            <div className="mt-6">
              <button
                type="button"
                onClick={() => setIsEvidenceBrowserOpen(open => !open)}
                aria-expanded={isEvidenceBrowserOpen}
                className="px-4 py-2 border border-line rounded-lg text-sm font-semibold text-text hover:bg-panel2 transition-colors"
              >
                Browse all screenshots {isEvidenceBrowserOpen ? '▾' : '▸'}
              </button>
              {isEvidenceBrowserOpen && (
                <div className="mt-3">
                  <EvidenceBrowser
                    evidenceItems={evidenceItems}
                    onOpenMedia={openLightbox}
                    onOpenTest={openTestSummary}
                  />
                </div>
              )}
            </div>

            {filteredUnmappedTests.length > 0 && (
              <div className="mt-6 border border-warn/25 bg-warn/10 rounded-lg p-5">
                <h3 className="text-lg font-serif text-text mb-2">Unmapped Tests</h3>
                <p className="text-sm text-text-dim mb-4">
                  These tests run in CI but are not yet tied to a user journey. That may be fine for lower-level
                  technical coverage, but they should be reviewed.
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                  {filteredUnmappedTests.map(test => (
                    <TestEvidenceRow
                      key={test.key}
                      test={test}
                      compact
                      onOpenMedia={openLightbox}
                      onOpenTest={() => openTestSummary({ journey: null, test })}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <section className="space-y-4">
        {status?.suites.length ? (
          <div>
            <h2 className="text-2xl font-serif text-text">GitHub CI Suites</h2>
            <p className="mt-1 max-w-3xl text-sm text-text-dim">
              {isUsingLocalReview
                ? 'These job cards come from the latest GitHub Actions run. The journey review above is using the current local report.'
                : 'These job cards come from the latest GitHub Actions run.'}
            </p>
          </div>
        ) : null}
        {status?.suites.map(suite => (
          <article key={suite.key} className="border border-line rounded-lg bg-panel overflow-hidden">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between p-5 border-b border-line-soft">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-serif text-text">{suite.label}</h2>
                  <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${statusClass(suite.status, suite.conclusion)}`}>
                    {statusLabel(suite.status, suite.conclusion)}
                  </span>
                </div>
                <code className="block mt-2 text-xs text-text-dim bg-bg border border-line-soft rounded px-2 py-1 w-fit">
                  {suite.command}
                </code>
              </div>
              <div className="text-sm text-text-dim md:text-right">
                <div>Started {formatShortDateTime(suite.startedAt)}</div>
                <div>Duration {formatDuration(suite.durationSeconds)}</div>
                {suite.jobUrl && (
                  <a href={suite.jobUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                    Open job
                  </a>
                )}
              </div>
            </div>

            <div className="grid gap-0 lg:grid-cols-[1fr_1.2fr]">
              <div className="p-5 border-b lg:border-b-0 lg:border-r border-line-soft">
                <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-3">Pass criteria</div>
                <ul className="space-y-2 text-sm text-text-dim">
                  {suite.criteria.map(criterion => (
                    <li key={criterion} className="flex gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-text-faint shrink-0" />
                      <span>{criterion}</span>
                    </li>
                  ))}
                </ul>

                {suite.artifacts.length > 0 && (
                  <div className="mt-6">
                    <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-3">Artifacts</div>
                    <div className="space-y-2">
                      {suite.artifacts.map(artifact => (
                        <button
                          key={artifact.id}
                          type="button"
                          onClick={() => downloadArtifact(artifact)}
                          className="w-full flex items-center justify-between gap-3 border border-line-soft rounded-lg px-3 py-2 text-sm hover:bg-panel2 transition-colors text-left"
                        >
                          <span className="font-semibold text-text">{artifact.name}</span>
                          <span className="text-text-dim">{formatBytes(artifact.sizeBytes)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-5">
                <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-3">Job steps</div>
                {suite.steps.length === 0 ? (
                  <div className="text-sm text-text-dim">No job details available yet.</div>
                ) : (
                  <div className="space-y-2">
                    {suite.steps.map(step => (
                      <div key={`${suite.key}-${step.name}`} className="flex items-center justify-between gap-3 text-sm border border-line-soft rounded-lg px-3 py-2">
                        <div>
                          <div className="font-semibold text-text">{step.name}</div>
                          <div className="text-xs text-text-faint">{formatDuration(step.durationSeconds)}</div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${statusClass(step.status, step.conclusion)}`}>
                          {statusLabel(step.status, step.conclusion)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}
      </section>

      {lightboxItem && (
        <MediaLightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />
      )}

      {selectedTestItem && (
        <TestSummaryModal
          item={selectedTestItem}
          request={testChangeRequest}
          isCreatingPlan={isCreatingTestChangePlan}
          notice={testChangePlanNotice}
          error={testChangePlanError}
          isLightboxOpen={Boolean(lightboxItem)}
          onChangeRequest={setTestChangeRequest}
          onCreatePlan={() => void createTestChangePlan()}
          onOpenMedia={openLightbox}
          onClose={() => setSelectedTestItem(null)}
        />
      )}
    </div>
  );
}

function ReviewMetric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="border border-line rounded-lg bg-panel p-4">
      <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold">{label}</div>
      <div className="text-2xl font-serif text-text mt-1">{value}</div>
    </div>
  );
}

function FailureReasonsPanel({
  items,
  onOpenTest,
}: {
  items: ReviewEvidenceItem[];
  onOpenTest: (item: ReviewEvidenceItem) => void;
}) {
  return (
    <section className="mb-4 rounded-lg border border-red-500/25 bg-red-500/10 p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-serif text-text">Failed Tests</h3>
        <div className="text-sm text-red-700">
          {items.length} failed test{items.length === 1 ? '' : 's'}
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {items.map((item) => (
          <button
            key={`${item.journey?.id ?? 'unmapped'}-${item.test.key}`}
            type="button"
            onClick={() => onOpenTest(item)}
            className="block w-full rounded-lg border border-red-500/20 bg-bg p-3 text-left transition-colors hover:border-red-500/40 hover:bg-panel focus:outline-none focus:ring-2 focus:ring-red-500/20"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="font-semibold text-text text-sm">{item.test.title}</div>
                <div className="mt-1 text-xs text-text-faint">
                  {item.journey?.label ?? 'Unmapped test'} · {item.test.file}
                </div>
                <TestReferenceMeta test={item.test} />
              </div>
              <span className={`w-fit rounded-full border px-2 py-0.5 text-xs font-semibold ${reviewStatusClass(item.test.status)}`}>
                {reviewStatusLabel(item.test.status)}
              </span>
            </div>
            <div className="mt-3 text-sm text-red-700">
              {getFailureReason(item.test)}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function EvidenceBrowser({
  evidenceItems,
  onOpenMedia,
  onOpenTest,
}: {
  evidenceItems: ReviewEvidenceItem[];
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
  onOpenTest: (item: ReviewEvidenceItem) => void;
}) {
  if (evidenceItems.length === 0) {
    return (
      <div className="border border-line rounded-lg bg-panel p-5 mb-4 text-sm text-text-dim">
        No tests match the current evidence filters.
      </div>
    );
  }

  return (
    <section className="border border-line rounded-lg bg-panel p-5 mb-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between mb-4">
        <div>
          <h3 className="text-xl font-serif text-text">Evidence browser</h3>
          <p className="text-sm text-text-dim mt-1">
            Browse tests from the latest Playwright run, including screenshots, videos, missing evidence notes, and feedback.
          </p>
        </div>
        <div className="text-sm text-text-dim">
          {evidenceItems.length} test{evidenceItems.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {evidenceItems.map(({ journey, test }) => (
          <EvidenceCard
            key={`${journey?.id ?? 'unmapped'}-${test.key}`}
            journey={journey}
            test={test}
            onOpenMedia={onOpenMedia}
            onOpenTest={() => onOpenTest({ journey, test })}
          />
        ))}
      </div>
    </section>
  );
}

function EvidenceCard({
  journey,
  test,
  onOpenMedia,
  onOpenTest,
}: {
  journey: TestReviewJourney | null;
  test: TestReviewEvidence;
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
  onOpenTest: () => void;
}) {
  const media = getTestMedia(test);
  const screenshots = getTestScreenshots(test);
  const video = media.find(item => getMediaKind(item) === 'video') ?? test.video ?? null;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpenTest}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenTest();
        }
      }}
      className="overflow-hidden rounded-lg border border-line-soft bg-bg text-left transition-colors hover:border-text/40 focus:outline-none focus:ring-2 focus:ring-text/20"
    >
      <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <ScreenshotGalleryPanel
          screenshots={screenshots}
          fallback={test.screenshotUnavailableReason}
          test={test}
          onOpenMedia={onOpenMedia}
        />
        <MediaPanel
          title="Video"
          media={video ?? null}
          fallback={test.videoUnavailableReason}
          alt={`Video for ${test.title}`}
          test={test}
          onOpenMedia={onOpenMedia}
        />
      </div>
      <div className="border-t border-line-soft p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold capitalize ${reviewStatusClass(test.status)}`}>
            {reviewStatusLabel(test.status)}
          </span>
          {test.testId && (
            <span className="rounded-full border border-line bg-panel px-2 py-0.5 text-xs font-semibold text-text-dim">
              {test.testId}
            </span>
          )}
          {journey && (
            <span className="rounded-full border border-line bg-panel px-2 py-0.5 text-xs font-semibold capitalize text-text-dim">
              {journey.role}
            </span>
          )}
          {test.flow && <FlowBadge flow={test.flow} />}
        </div>
        <div className="mt-2 font-semibold text-text text-sm">{test.title}</div>
        <div className="mt-1 text-xs text-text-faint">{journey?.label ?? 'Unmapped test'} · {test.file}</div>
        <TestReferenceMeta test={test} />
        <p className="mt-3 text-sm text-text-dim">{test.description}</p>
        <ScreenshotIssuesList issues={test.screenshotIssues ?? []} />
      </div>
    </article>
  );
}

function ScreenshotGalleryPanel({
  screenshots,
  fallback,
  test,
  onOpenMedia,
}: {
  screenshots: TestReviewScreenshot[];
  fallback?: string | null;
  test: TestReviewEvidence;
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
}) {
  return (
    <div className="min-h-[220px] border-b border-line-soft bg-[#0a0807] md:border-b-0 md:border-r">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 text-xs text-white/70">
        <span className="font-semibold">Screenshots</span>
        <span>{screenshots.length} screen{screenshots.length === 1 ? '' : 's'}</span>
      </div>
      {screenshots.length > 0 ? (
        <div className="max-h-[270px] space-y-2 overflow-auto p-2">
          {screenshots.map((screenshot, index) => {
            const title = screenshot.label || `Screen ${index + 1}`;
            return (
              <div
                key={`${screenshot.path}-${index}`}
                className="flex gap-2 rounded border border-white/10 bg-black/40 p-2"
              >
                {screenshot.dataUrl ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenMedia({ ...screenshot, kind: 'screenshot' }, test, title);
                    }}
                    className="group h-fit w-40 shrink-0 overflow-hidden rounded border border-white/10 bg-black text-left transition-colors hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/60"
                  >
                    <img
                      src={screenshot.dataUrl}
                      alt={`${title} for ${test.title}`}
                      className="h-24 w-full object-cover transition-transform group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </button>
                ) : (
                  <div className="flex h-24 w-40 shrink-0 items-center justify-center rounded border border-white/10 bg-black/60 p-2 text-center text-[10px] leading-4 text-white/50">
                    {screenshot.omittedReason || 'Screenshot is not available inline.'}
                  </div>
                )}
                <div className="min-w-0 flex-1 text-[11px] leading-4 text-white/70">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-semibold text-white/80">{title}</span>
                    <span className="shrink-0 text-white/45">{formatBytes(screenshot.sizeBytes)}</span>
                  </div>
                  <ScreenshotAnnotationMeta screenshot={screenshot} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex h-[220px] items-center justify-center p-4 text-center text-xs leading-5 text-white/60">
          {fallback || 'Screenshots are not available for this test.'}
        </div>
      )}
    </div>
  );
}

function ScreenshotAnnotationMeta({ screenshot }: { screenshot: TestReviewScreenshot }) {
  const checks = screenshot.checks ?? [];
  const hasStepBadge = screenshot.stepIndex !== null && screenshot.stepIndex !== undefined;
  const pageUrl = normalizeReviewPageUrl(screenshot.pageUrl);
  if (!hasStepBadge && !screenshot.annotated && !screenshot.caption && !screenshot.cropWarning && !pageUrl && !screenshot.capturedAt && checks.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 text-[11px] leading-4">
      {(hasStepBadge || screenshot.annotated || screenshot.cropWarning) && (
        <div className="flex flex-wrap items-center gap-1">
          {hasStepBadge && (
            <span className="rounded-full border border-white/20 bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-white/80">
              Step {screenshot.stepIndex}
            </span>
          )}
          {screenshot.annotated && (
            <span className="rounded-full border border-white/15 bg-white/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/60">
              Annotated
            </span>
          )}
          {screenshot.cropWarning && (
            <span
              title={screenshot.cropWarning}
              className="rounded-full border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300"
            >
              Crop warning
            </span>
          )}
        </div>
      )}
      {checks.length > 0 ? (
        <div className="mt-1.5">
          <CheckList checks={checks} tone="dark" />
        </div>
      ) : screenshot.caption ? (
        <div className="mt-1 text-white/70">{screenshot.caption}</div>
      ) : null}
      {screenshot.capturedAt && (
        <div className="mt-1 text-white/45">
          Captured {formatShortDateTime(screenshot.capturedAt)}
        </div>
      )}
      {pageUrl && (
        <a
          href={pageUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => event.stopPropagation()}
          className="mt-1 inline-block break-all text-white/80 underline decoration-white/30 underline-offset-2 hover:text-white"
        >
          Open page: {pageUrl}
        </a>
      )}
    </div>
  );
}

function ScreenshotIssuesList({ issues }: { issues: string[] }) {
  if (issues.length === 0) return null;

  return (
    <div className="mt-3 rounded border border-warn/25 bg-warn/10 p-3 text-xs leading-5 text-text">
      <div className="font-semibold text-warn">
        Screenshot issue{issues.length === 1 ? '' : 's'}
      </div>
      <ul className="mt-1 space-y-1 text-text-dim">
        {issues.map(issue => (
          <li key={issue} className="flex gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-warn" />
            <span>{issue}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MediaPanel({
  title,
  media,
  fallback,
  alt,
  test,
  onOpenMedia,
}: {
  title: string;
  media: TestReviewMedia | null;
  fallback?: string | null;
  alt: string;
  test: TestReviewEvidence;
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
}) {
  const kind = media ? getMediaKind(media) : null;

  return (
    <div className="min-h-[220px] border-b border-line-soft bg-[#0a0807] md:border-b-0 md:border-r">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 text-xs text-white/70">
        <span className="font-semibold">{title}</span>
        {media && <span>{formatBytes(media.sizeBytes)}</span>}
      </div>
      {media?.dataUrl && kind === 'video' ? (
        <video
          src={media.dataUrl}
          controls
          preload="metadata"
          onClick={(event) => event.stopPropagation()}
          className="h-[220px] w-full bg-black object-contain"
        />
      ) : media?.dataUrl ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenMedia(media, test, media.label || title);
          }}
          className="block w-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/70"
        >
          <img
            src={media.dataUrl}
            alt={alt}
            className="h-[220px] w-full object-contain"
            loading="lazy"
          />
        </button>
      ) : (
        <div className="flex h-[220px] items-center justify-center p-4 text-center text-xs leading-5 text-white/60">
          {media?.omittedReason || fallback || `${title} is not available for this test.`}
        </div>
      )}
    </div>
  );
}

function JourneyIndexCard({
  journey,
  tests,
  isFiltered,
  onExpand,
}: {
  journey: TestReviewJourney;
  tests: TestReviewEvidence[];
  isFiltered: boolean;
  onExpand: () => void;
}) {
  const flow = getJourneyFlow(journey);

  return (
    <button
      type="button"
      onClick={onExpand}
      aria-expanded={false}
      className="border border-line rounded-lg bg-panel p-4 text-left transition-colors hover:border-text/40 focus:outline-none focus:ring-2 focus:ring-text/20"
    >
      <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold">
        {journey.role} journey
      </div>
      <div className="mt-1 text-lg font-serif text-text">{journey.label}</div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="px-2 py-0.5 rounded-full border border-line bg-bg text-xs font-semibold text-text-dim capitalize">
          {journey.coverageStatus}
        </span>
        <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold capitalize ${reviewStatusClass(journey.resultStatus)}`}>
          {reviewStatusLabel(journey.resultStatus)}
        </span>
        <span className="text-xs text-text-dim">
          {tests.length} {isFiltered ? 'matching ' : ''}test{tests.length === 1 ? '' : 's'}
        </span>
        {flow && (
          flow.status === 'failed' ? (
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-red-500/25 bg-red-500/10 px-2 py-0.5 text-[11px] font-semibold text-red-600">
              Flow failed at step {flow.failedStepIndex ?? '?'} of {flow.stepCount}
            </span>
          ) : (
            <FlowBadge flow={flow} />
          )
        )}
      </div>
    </button>
  );
}

function JourneyDetailCard({
  journey,
  tests,
  isFiltered,
  onOpenMedia,
  onOpenTest,
  onCollapse,
}: {
  journey: TestReviewJourney;
  tests: TestReviewEvidence[];
  isFiltered: boolean;
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
  onOpenTest: (test: TestReviewEvidence) => void;
  onCollapse: () => void;
}) {
  const flowTests = journey.tests.filter(
    (test): test is TestReviewEvidence & { flow: TestReviewFlow } => Boolean(test.flow)
  );

  return (
    <article className="border border-line rounded-lg bg-panel overflow-hidden">
      <div className="p-5 border-b border-line-soft">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-1">
              {journey.role} journey
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-xl font-serif text-text">{journey.label}</h3>
              <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold capitalize ${reviewStatusClass(journey.resultStatus)}`}>
                {reviewStatusLabel(journey.resultStatus)}
              </span>
              <span className="px-2 py-0.5 rounded-full border border-line bg-bg text-xs font-semibold text-text-dim capitalize">
                {journey.coverageStatus}
              </span>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="text-sm text-text-dim md:text-right">
              <div>
                {tests.length} {isFiltered ? 'matching ' : ''}test{tests.length === 1 ? '' : 's'}
              </div>
              <div>{journey.gaps.length} gap{journey.gaps.length === 1 ? '' : 's'}</div>
            </div>
            <button
              type="button"
              onClick={onCollapse}
              className="rounded-lg border border-line px-3 py-1.5 text-sm font-semibold text-text transition-colors hover:bg-panel2"
            >
              Collapse
            </button>
          </div>
        </div>

        <p className="text-sm text-text mt-4 max-w-4xl">{journey.goal}</p>
        {journey.expectedOutcome && (
          <p className="text-sm text-text-dim mt-2 max-w-4xl">
            <span className="font-semibold text-text">Expected outcome: </span>
            {journey.expectedOutcome}
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {journey.entryPoints.map(entry => (
            <span key={entry} className="text-xs border border-line-soft rounded px-2 py-1 text-text-dim bg-bg">
              {entry}
            </span>
          ))}
        </div>
      </div>

      {flowTests.length > 0 && (
        <div className="p-5 border-b border-line-soft space-y-4">
          {flowTests.map(test => (
            <FlowJourneyPath key={test.key} flow={test.flow} test={test} onOpenMedia={onOpenMedia} />
          ))}
        </div>
      )}

      <div className="grid gap-0 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="p-5 border-b lg:border-b-0 lg:border-r border-line-soft">
          {journey.criticalSteps.length > 0 && (
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-3">Critical steps</div>
              <ol className="space-y-2 text-sm text-text-dim">
                {journey.criticalSteps.map((step, index) => (
                  <li key={step} className="flex gap-2">
                    <span className="shrink-0 text-text-faint">{index + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-3">Review criteria</div>
          <ul className="space-y-2 text-sm text-text-dim">
            {journey.reviewCriteria.map(criterion => (
              <li key={criterion} className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-text-faint shrink-0" />
                <span>{criterion}</span>
              </li>
            ))}
          </ul>

          {journey.gaps.length > 0 && (
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-3">Known gaps</div>
              <div className="space-y-3">
                {journey.gaps.map(gap => (
                  <div key={gap.id} className="border border-warn/25 bg-warn/10 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-text text-sm">{gap.title}</div>
                      <span className="text-xs uppercase tracking-wide text-warn font-semibold">{gap.severity}</span>
                    </div>
                    <p className="text-sm text-text-dim mt-2">{gap.currentBehavior}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold mb-3">Evidence</div>
          {tests.length === 0 ? (
            <div className="text-sm text-text-dim">No tests are mapped to this journey yet.</div>
          ) : (
            <div className="space-y-3">
              {tests.map(test => (
                <TestEvidenceRow
                  key={test.key}
                  test={test}
                  onOpenMedia={onOpenMedia}
                  onOpenTest={() => onOpenTest(test)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function TestEvidenceRow({
  test,
  compact = false,
  onOpenMedia,
  onOpenTest,
}: {
  test: TestReviewEvidence;
  compact?: boolean;
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
  onOpenTest: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpenTest}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenTest();
        }
      }}
      className="border border-line-soft rounded-lg overflow-hidden bg-bg text-left transition-colors hover:border-text/40 focus:outline-none focus:ring-2 focus:ring-text/20"
    >
      <div className={`grid gap-0 ${compact ? '' : 'md:grid-cols-[180px_1fr]'}`}>
        {!compact && (
          <ScreenshotPreview test={test} onOpenMedia={onOpenMedia} />
        )}
        <div className="p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="font-semibold text-text text-sm">{test.title}</div>
              <div className="text-xs text-text-faint mt-1">{test.file}</div>
              <TestReferenceMeta test={test} />
              {test.flow && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <FlowBadge flow={test.flow} />
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${flowSummaryClass(test.flow)}`}>
                    {flowSummaryLabel(test.flow)}
                  </span>
                </div>
              )}
            </div>
            <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold capitalize w-fit ${reviewStatusClass(test.status)}`}>
              {reviewStatusLabel(test.status)}
            </span>
          </div>
          <p className="text-sm text-text-dim mt-3">{test.description}</p>
          <div className="text-xs text-text-faint mt-3">
            Duration {formatMilliseconds(test.durationMs)}
          </div>
          <ScreenshotIssuesList issues={test.screenshotIssues ?? []} />
          {isFailedReviewStatus(test.status) && (
            <div className="mt-3 rounded border border-red-500/20 bg-red-500/10 p-3 text-xs leading-5 text-red-700">
              <span className="font-semibold">Reason: </span>{getFailureReason(test)}
            </div>
          )}
          {test.error && (
            <pre className="mt-3 max-h-32 overflow-auto whitespace-pre-wrap rounded border border-red-500/20 bg-red-500/10 p-3 text-xs text-red-700">
              {test.error}
            </pre>
          )}
          {compact && test.screenshotUnavailableReason && (
            <div className="mt-3 text-xs text-text-faint">{test.screenshotUnavailableReason}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function TestReferenceMeta({ test }: { test: TestReviewEvidence }) {
  const items = [
    test.testId ? `ID ${test.testId}` : null,
    test.startedAt ? `Ran ${formatShortDateTime(test.startedAt)}` : null,
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-faint">
      {items.map((item) => (
        <span key={item}>{item}</span>
      ))}
    </div>
  );
}

function FlowBadge({ flow }: { flow: TestReviewFlow }) {
  return (
    <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent">
      Journey flow · {flow.stepCount} step{flow.stepCount === 1 ? '' : 's'}
    </span>
  );
}

function FlowJourneyPath({
  flow,
  test,
  onOpenMedia,
}: {
  flow: TestReviewFlow;
  test: TestReviewEvidence;
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
}) {
  const screenshotsByLabel = new Map<string, TestReviewScreenshot>();
  for (const screenshot of getTestScreenshots(test)) {
    if (screenshot.label) screenshotsByLabel.set(screenshot.label, screenshot);
  }

  return (
    <section className="rounded-lg border border-line bg-bg p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-text-faint font-semibold">Journey path</div>
          <h3 className="mt-1 text-lg font-serif text-text">{flow.goal}</h3>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-faint">
            <span className="capitalize">{flow.role}</span>
            {flow.persona && <span>{flow.persona}</span>}
            <span className="break-all">{flow.id}</span>
            <span>{formatMilliseconds(flow.durationMs)}</span>
          </div>
        </div>
        <span className={`w-fit rounded-full border px-2.5 py-1 text-xs font-semibold ${flowSummaryClass(flow)}`}>
          {flowSummaryLabel(flow)}
        </span>
      </div>

      <ol className="mt-4 space-y-3">
        {flow.steps.map((step) => {
          const route = step.route ?? step.expectedRoute;
          const isFailed = step.status === 'failed';
          const isNotReached = step.status === 'not-reached';
          const stepChecks = step.checks ?? [];

          return (
            <li
              key={step.index}
              className={`rounded-lg border p-3 ${
                isFailed
                  ? 'border-red-500/25 border-l-4 border-l-red-500 bg-red-500/5'
                  : isNotReached
                    ? 'border-line-soft bg-panel opacity-60'
                    : 'border-line-soft bg-panel'
              }`}
            >
              <div className="flex gap-3">
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${flowStepStatusClass(step.status)}`}>
                  {step.index}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-text">{step.title}</span>
                    {route && (
                      <code className="rounded border border-line-soft bg-bg px-1.5 py-0.5 text-[11px] text-text-dim">
                        {route}
                      </code>
                    )}
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${flowStepStatusClass(step.status)}`}>
                      {flowStepStatusLabel(step.status)}
                    </span>
                    <span className="text-[11px] text-text-faint">{formatMilliseconds(step.durationMs)}</span>
                  </div>
                  {stepChecks.length > 0 ? (
                    <p className="mt-1 truncate text-[11px] leading-4 text-text-faint" title={step.verify}>
                      {step.verify}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs leading-5 text-text-dim">{step.verify}</p>
                  )}
                  {isFailed && step.error && (
                    <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded border border-red-500/20 bg-red-500/10 p-2 text-xs text-red-700">
                      {step.error}
                    </pre>
                  )}
                  {(step.screenshots.length > 0 || stepChecks.length > 0) && (
                    <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                      {step.screenshots.length > 0 && (
                        <div className="flex w-40 shrink-0 flex-col gap-2">
                          {step.screenshots.map((name) => {
                            const screenshot = screenshotsByLabel.get(name) ?? null;
                            return screenshot?.dataUrl ? (
                              <button
                                key={name}
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onOpenMedia({ ...screenshot, kind: 'screenshot' }, test, name);
                                }}
                                className="group overflow-hidden rounded border border-line-soft bg-black text-left transition-colors hover:border-text/40 focus:outline-none focus:ring-2 focus:ring-text/20"
                              >
                                <img
                                  src={screenshot.dataUrl}
                                  alt={`${name} for ${test.title}`}
                                  className="h-24 w-full object-cover transition-transform group-hover:scale-[1.03]"
                                  loading="lazy"
                                />
                              </button>
                            ) : (
                              <span
                                key={name}
                                className="break-all rounded border border-line-soft bg-panel2 px-2 py-1 text-[11px] text-text-faint"
                                title={screenshot?.omittedReason ?? 'Screenshot is not embedded in this report.'}
                              >
                                {name}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {stepChecks.length > 0 && (
                        <div className="min-w-0 flex-1">
                          <CheckList checks={stepChecks} tone="light" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function buildTestChangePlanDraft({
  item,
  request,
}: {
  item: ReviewEvidenceItem;
  request: string;
}): TestChangePlanDraft {
  const test = item.test;
  const testId = test.testId || test.key;
  const journeyLabel = item.journey?.label ?? 'Unmapped test';
  const body = [
    `Requested change`,
    request.trim(),
    '',
    `Test reference`,
    `- Test ID: ${testId}`,
    `- Title: ${test.title}`,
    `- File: ${test.file}`,
    `- Key: ${test.key}`,
    `- Status: ${test.status}`,
    `- Ran at: ${test.startedAt ?? 'unknown'}`,
    `- Duration: ${formatMilliseconds(test.durationMs)}`,
    `- Journey: ${journeyLabel}`,
    item.journey ? `- Role: ${item.journey.role}` : null,
    '',
    `Current test description`,
    test.description,
    '',
    `Current outcome`,
    test.outcome,
    test.error ? ['', `Current error`, test.error] : null,
    '',
    `Review criteria`,
    ...(test.criteria.length ? test.criteria.map((criterion) => `- ${criterion}`) : ['- No criteria recorded.']),
  ]
    .flat()
    .filter((line): line is string => line !== null)
    .join('\n');

  return {
    title: `Test edit: ${testId}`,
    summary: `${test.title} (${test.file})`,
    body,
    planType: 'external_harness',
  };
}

function TestSummaryModal({
  item,
  request,
  isCreatingPlan,
  notice,
  error,
  isLightboxOpen,
  onChangeRequest,
  onCreatePlan,
  onOpenMedia,
  onClose,
}: {
  item: ReviewEvidenceItem;
  request: string;
  isCreatingPlan: boolean;
  notice: TestChangePlanNotice | null;
  error: string | null;
  isLightboxOpen: boolean;
  onChangeRequest: (value: string) => void;
  onCreatePlan: () => void;
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
  onClose: () => void;
}) {
  const test = item.test;
  const media = getTestMedia(test);
  const screenshots = getTestScreenshots(test);
  const video = media.find(candidate => getMediaKind(candidate) === 'video') ?? test.video ?? null;
  const testId = test.testId || test.key;

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (isLightboxOpen) return;
      if (event.key === 'Escape') onClose();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Test summary for ${test.title}`}
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 p-4"
    >
      <div className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-line bg-panel shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${reviewStatusClass(test.status)}`}>
                {reviewStatusLabel(test.status)}
              </span>
              <span className="rounded-full border border-line bg-bg px-2 py-0.5 text-xs font-semibold text-text-dim">
                {testId}
              </span>
              {item.journey && (
                <span className="rounded-full border border-line bg-bg px-2 py-0.5 text-xs font-semibold capitalize text-text-dim">
                  {item.journey.role}
                </span>
              )}
              {test.flow && <FlowBadge flow={test.flow} />}
            </div>
            <h2 className="mt-2 text-2xl font-serif text-text">{test.title}</h2>
            <div className="mt-1 text-xs text-text-faint">
              {item.journey?.label ?? 'Unmapped test'} · {test.file}
            </div>
            <TestReferenceMeta test={test} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-line px-3 py-2 text-sm font-semibold text-text transition-colors hover:bg-bg"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-5">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
            <div className="space-y-5">
              {test.flow && (
                <FlowJourneyPath flow={test.flow} test={test} onOpenMedia={onOpenMedia} />
              )}

              <section className="rounded-lg border border-line bg-bg p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-lg font-serif text-text">Evidence</h3>
                  <span className="text-xs text-text-faint">
                    {screenshots.length} screenshot{screenshots.length === 1 ? '' : 's'} · {video ? '1 video' : 'no video'}
                  </span>
                </div>

                {video?.dataUrl ? (
                  <button
                    type="button"
                    onClick={() => onOpenMedia(video, test, video.label || 'Video')}
                    className="group relative mb-4 block w-full overflow-hidden rounded-lg border border-line-soft bg-black text-left transition-colors hover:border-text/40 focus:outline-none focus:ring-2 focus:ring-text/20"
                  >
                    <video
                      src={video.dataUrl}
                      preload="metadata"
                      muted
                      playsInline
                      className="max-h-[360px] w-full bg-black object-contain"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 transition-opacity group-hover:bg-black/30">
                      <span className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white shadow-lg">
                        Open video
                      </span>
                    </div>
                  </button>
                ) : video ? (
                  <div className="mb-4 rounded-lg border border-line-soft bg-panel2 p-4 text-sm text-text-dim">
                    {video.omittedReason || 'Video exists but is not available inline.'}
                  </div>
                ) : null}

                {screenshots.length > 0 ? (
                  <div className="space-y-3">
                    {screenshots.map((screenshot, index) => {
                      const title = screenshot.label || `Screenshot ${index + 1}`;
                      return (
                        <div
                          key={`${screenshot.path}-${index}`}
                          className="flex gap-3 rounded-lg border border-line-soft bg-[#0a0807] p-2"
                        >
                          {screenshot.dataUrl ? (
                            <button
                              type="button"
                              onClick={() => onOpenMedia({ ...screenshot, kind: 'screenshot' }, test, title)}
                              className="group h-fit w-48 shrink-0 overflow-hidden rounded border border-white/10 bg-black text-left transition-colors hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/60"
                            >
                              <img
                                src={screenshot.dataUrl}
                                alt={`${title} for ${test.title}`}
                                className="h-32 w-full object-cover transition-transform group-hover:scale-[1.02]"
                                loading="lazy"
                              />
                            </button>
                          ) : (
                            <div className="flex h-32 w-48 shrink-0 items-center justify-center rounded border border-white/10 bg-black/60 p-3 text-center text-xs leading-5 text-white/55">
                              {screenshot.omittedReason || 'Screenshot is not available inline.'}
                            </div>
                          )}
                          <div className="min-w-0 flex-1 py-1 text-xs leading-4 text-white/70">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate font-semibold text-white/85">{title}</span>
                              <span className="shrink-0 text-white/45">{formatBytes(screenshot.sizeBytes)}</span>
                            </div>
                            <ScreenshotAnnotationMeta screenshot={screenshot} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-lg border border-line-soft bg-panel2 p-4 text-sm text-text-dim">
                    {test.screenshotUnavailableReason || 'No screenshots are available for this test.'}
                  </div>
                )}
                <ScreenshotIssuesList issues={test.screenshotIssues ?? []} />
              </section>

              <section className="rounded-lg border border-line bg-bg p-4">
                <h3 className="text-lg font-serif text-text">What This Test Checks</h3>
                <p className="mt-3 text-sm leading-6 text-text-dim">{test.description}</p>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-faint">Outcome</div>
                    <p className="mt-1 leading-6 text-text-dim">{test.outcome}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-faint">Duration</div>
                    <p className="mt-1 text-text-dim">{formatMilliseconds(test.durationMs)}</p>
                  </div>
                </div>
                {test.error && (
                  <pre className="mt-4 max-h-48 overflow-auto whitespace-pre-wrap rounded border border-red-500/20 bg-red-500/10 p-3 text-xs text-red-700">
                    {test.error}
                  </pre>
                )}
                {test.criteria.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-text-faint">Criteria</div>
                    <ul className="mt-2 space-y-2 text-sm text-text-dim">
                      {test.criteria.map((criterion) => (
                        <li key={criterion} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-text-faint" />
                          <span>{criterion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            </div>

            <aside className="space-y-4">
              <section className="rounded-lg border border-line bg-bg p-4">
                <h3 className="text-lg font-serif text-text">Request Test Changes</h3>
                <p className="mt-2 text-sm leading-6 text-text-dim">
                  Write what should change about this test, its screenshots, or its review criteria. This creates a draft plan for later review.
                </p>
                <textarea
                  value={request}
                  onChange={(event) => onChangeRequest(event.target.value)}
                  className="input mt-4 min-h-[180px] w-full resize-y text-sm leading-5"
                  placeholder="Example: This should verify the actual checkout confirmation screen, not just the dashboard redirect."
                />
                {error && (
                  <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                )}
                {notice && (
                  <div className="mt-3 rounded-lg border border-ok/20 bg-ok/10 px-3 py-2 text-sm text-ok">
                    {notice.message}{' '}
                    <a
                      href={`/admin/plans?planId=${encodeURIComponent(notice.planId)}&status=${encodeURIComponent(notice.planStatus)}`}
                      className="font-semibold underline decoration-ok/40 underline-offset-2 hover:decoration-ok"
                    >
                      Open plan
                    </a>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  <a href="/admin/plans" className="btn btn-sm">
                    View Plans
                  </a>
                  <button
                    type="button"
                    onClick={onCreatePlan}
                    className="btn btn-accent btn-sm"
                    disabled={isCreatingPlan || !request.trim()}
                  >
                    {isCreatingPlan ? 'Creating...' : 'Create draft plan'}
                  </button>
                </div>
              </section>

              <section className="rounded-lg border border-line bg-bg p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-faint">Raw Reference</div>
                <dl className="mt-3 space-y-3 text-sm">
                  <div>
                    <dt className="text-text-faint">Test ID</dt>
                    <dd className="break-all text-text">{testId}</dd>
                  </div>
                  <div>
                    <dt className="text-text-faint">Key</dt>
                    <dd className="break-all text-text-dim">{test.key}</dd>
                  </div>
                  <div>
                    <dt className="text-text-faint">File</dt>
                    <dd className="break-all text-text-dim">{test.file}</dd>
                  </div>
                </dl>
              </section>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScreenshotPreview({
  test,
  onOpenMedia,
}: {
  test: TestReviewEvidence;
  onOpenMedia: (media: TestReviewMedia, test: TestReviewEvidence, title?: string) => void;
}) {
  const screenshots = getTestScreenshots(test);
  const screenshot = screenshots.find(item => item.dataUrl) ?? screenshots[0] ?? null;

  if (screenshot?.dataUrl) {
    return (
      <div className="bg-[#0a0807] min-h-[120px]">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenMedia({ ...screenshot, kind: 'screenshot' }, test, screenshot.label || 'Screenshot');
          }}
          className="group relative block h-full min-h-[120px] w-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/70"
        >
          <img
            src={screenshot.dataUrl}
            alt={`Screenshot for ${test.title}`}
            className="h-full min-h-[120px] w-full object-cover"
            loading="lazy"
          />
          {screenshots.length > 1 && (
            <span className="absolute bottom-2 right-2 rounded-full bg-black/70 px-2 py-0.5 text-xs font-semibold text-white">
              {screenshots.length} screens
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-[120px] bg-panel2 p-4 flex items-center justify-center text-center text-xs text-text-faint">
      {screenshot?.omittedReason || test.screenshotUnavailableReason || 'No screenshot available.'}
    </div>
  );
}

function MediaLightbox({ item, onClose }: { item: LightboxItem; onClose: () => void }) {
  const [index, setIndex] = useState(item.index);
  const [isZoomed, setIsZoomed] = useState(false);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setIndex(item.index);
    setIsZoomed(false);
  }, [item]);

  const entries = item.entries;
  const safeIndex = Math.min(Math.max(index, 0), Math.max(entries.length - 1, 0));
  const entry = entries[safeIndex];
  const media = entry.media;
  const kind = getMediaKind(media);
  const screenshot = kind === 'screenshot' ? (media as TestReviewScreenshot) : null;
  const screenshotPageUrl = normalizeReviewPageUrl(screenshot?.pageUrl);
  const checks = screenshot?.checks ?? [];
  const hasScreenshotMeta = Boolean(
    screenshot && (
      checks.length > 0
      || screenshot.caption
      || screenshotPageUrl
      || screenshot.capturedAt
      || screenshot.capture
      || screenshot.cropWarning
      || screenshot.annotated
      || (screenshot.stepIndex !== null && screenshot.stepIndex !== undefined)
    )
  );
  const isImageZoomed = isZoomed && kind === 'screenshot' && Boolean(media.dataUrl);
  const hasPrevious = safeIndex > 0;
  const hasNext = safeIndex < entries.length - 1;

  const goTo = useCallback((nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= entries.length) return;
    setIsZoomed(false);
    setIndex(nextIndex);
  }, [entries]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      event.preventDefault();
      goTo(event.key === 'ArrowLeft' ? safeIndex - 1 : safeIndex + 1);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goTo, safeIndex]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={entry.title}
      onClick={onClose}
      className="fixed inset-0 z-[70] flex flex-col bg-black/90 p-4 text-white"
    >
      <div className="mb-3 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <div className="truncate text-lg font-semibold">{entry.title}</div>
            {entries.length > 1 && (
              <span className="shrink-0 rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-xs font-semibold text-white/75">
                {safeIndex + 1} / {entries.length}
              </span>
            )}
          </div>
          <div className="mt-1 truncate text-sm text-white/65">{item.test.title} · {item.test.file}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-white/20 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10"
        >
          Close
        </button>
      </div>
      <div className="relative min-h-0 flex-1">
        <div
          className={`flex h-full w-full rounded-lg border border-white/10 bg-black ${
            isImageZoomed ? 'overflow-auto' : 'items-center justify-center overflow-hidden'
          }`}
        >
          {kind === 'video' && media.dataUrl ? (
            <video
              key={media.path}
              src={media.dataUrl}
              controls
              autoPlay
              onClick={(event) => event.stopPropagation()}
              className="max-h-full max-w-full"
            />
          ) : media.dataUrl ? (
            <img
              key={media.path}
              src={media.dataUrl}
              alt={`Screenshot for ${item.test.title}`}
              draggable={false}
              onPointerDown={(event) => {
                pointerStart.current = { x: event.clientX, y: event.clientY };
              }}
              onClick={(event) => {
                event.stopPropagation();
                const start = pointerStart.current;
                pointerStart.current = null;
                if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 6) return;
                setIsZoomed(zoomed => !zoomed);
              }}
              className={isImageZoomed
                ? 'm-auto max-w-none shrink-0 cursor-zoom-out select-none'
                : 'max-h-full max-w-full cursor-zoom-in select-none object-contain'}
            />
          ) : (
            <div
              onClick={(event) => event.stopPropagation()}
              className="max-w-lg p-6 text-center text-sm leading-6 text-white/70"
            >
              {media.omittedReason || 'This media is not available inline.'}
            </div>
          )}
        </div>
        {hasPrevious && (
          <button
            type="button"
            aria-label="Previous item"
            onClick={(event) => {
              event.stopPropagation();
              goTo(safeIndex - 1);
            }}
            className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/70 text-2xl leading-none text-white/85 shadow-lg transition-colors hover:bg-black/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            ‹
          </button>
        )}
        {hasNext && (
          <button
            type="button"
            aria-label="Next item"
            onClick={(event) => {
              event.stopPropagation();
              goTo(safeIndex + 1);
            }}
            className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/70 text-2xl leading-none text-white/85 shadow-lg transition-colors hover:bg-black/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            ›
          </button>
        )}
      </div>
      {hasScreenshotMeta && screenshot && (
        <div
          onClick={(event) => event.stopPropagation()}
          className="mt-3 rounded-lg border border-white/10 bg-black/60 p-3 text-xs leading-5 text-white/70"
        >
          <div className="flex flex-wrap items-center gap-2">
            {screenshot.stepIndex !== null && screenshot.stepIndex !== undefined && (
              <span className="rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/80">
                Step {screenshot.stepIndex}
              </span>
            )}
            {screenshot.annotated && (
              <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white/60">
                Annotated
              </span>
            )}
            {screenshot.capture && (
              <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-white/60">
                {screenshot.capture}
              </span>
            )}
          </div>
          {checks.length > 0 ? (
            <div className="mt-2">
              <div className="text-white/55">{checksSummary(checks)}</div>
              <div className="mt-1.5">
                <CheckList checks={checks} tone="dark" />
              </div>
            </div>
          ) : screenshot.caption ? (
            <div className="mt-2 text-sm text-white/85">{screenshot.caption}</div>
          ) : null}
          {screenshot.capturedAt && (
            <div className="mt-2 text-white/55">
              Captured {formatShortDateTime(screenshot.capturedAt)}
            </div>
          )}
          {screenshotPageUrl && (
            <a
              href={screenshotPageUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block break-all text-white/75 underline decoration-white/30 underline-offset-2 hover:text-white"
            >
              Open page: {screenshotPageUrl}
            </a>
          )}
          {screenshot.cropWarning && (
            <div className="mt-2 rounded border border-amber-400/40 bg-amber-400/10 px-2 py-1 text-amber-300">
              {screenshot.cropWarning}
            </div>
          )}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-white/60">
        <span>{media.fileName}</span>
        <span>{formatBytes(media.sizeBytes)}</span>
      </div>
    </div>
  );
}

function matchesReviewFilter(test: TestReviewEvidence, filter: ReviewFilter) {
  if (filter === 'all') return true;
  if (filter === 'failed') return isFailedReviewStatus(test.status);
  if (filter === 'needs-attention') return isNeedsAttentionReviewStatus(test.status);
  if (filter === 'screenshot-issues') return hasScreenshotIssues(test);
  return hasMissingScreenshots(test);
}

export function hasScreenshotIssues(test: TestReviewEvidence) {
  return (test.screenshotIssues?.length ?? 0) > 0;
}

export function hasMissingScreenshots(test: TestReviewEvidence) {
  const screenshots = getTestScreenshots(test);

  if (screenshots.length > 0) {
    return screenshots.every((screenshot) => !screenshot.dataUrl);
  }

  return Boolean(test.screenshotUnavailableReason);
}

function getFailureReason(test: TestReviewEvidence) {
  return test.error || test.outcome || 'No failure reason was reported by Playwright.';
}

function getTestMedia(test: TestReviewEvidence): TestReviewMedia[] {
  if (test.media?.length) return test.media;
  const screenshots = test.screenshots?.length
    ? test.screenshots.map(screenshot => ({ ...screenshot, kind: 'screenshot' as const }))
    : test.screenshot ? [{ ...test.screenshot, kind: 'screenshot' as const }] : [];
  return test.video ? [...screenshots, test.video] : screenshots;
}

function getTestScreenshots(test: TestReviewEvidence): TestReviewScreenshot[] {
  if (test.screenshots?.length) {
    return test.screenshots.map(screenshot => ({ ...screenshot, kind: 'screenshot' as const }));
  }

  const mediaScreenshots = (test.media ?? [])
    .filter((media): media is TestReviewScreenshot => getMediaKind(media) === 'screenshot')
    .map(screenshot => ({ ...screenshot, kind: 'screenshot' as const }));

  if (mediaScreenshots.length) return mediaScreenshots;
  return test.screenshot ? [{ ...test.screenshot, kind: 'screenshot' as const }] : [];
}

function getMediaKind(media: TestReviewMedia): 'screenshot' | 'video' {
  return media.kind === 'video' ? 'video' : 'screenshot';
}

export function getLightboxGalleryMedia(test: TestReviewEvidence): TestReviewMedia[] {
  const media = getTestMedia(test);
  if (!test.flow) return media;

  const screenshotsByLabel = new Map<string, TestReviewScreenshot>();
  for (const screenshot of getTestScreenshots(test)) {
    if (screenshot.label && !screenshotsByLabel.has(screenshot.label)) {
      screenshotsByLabel.set(screenshot.label, screenshot);
    }
  }

  const ordered: TestReviewMedia[] = [];
  const seenPaths = new Set<string>();
  for (const step of test.flow.steps) {
    for (const label of step.screenshots) {
      const screenshot = screenshotsByLabel.get(label);
      if (!screenshot || seenPaths.has(screenshot.path)) continue;
      seenPaths.add(screenshot.path);
      ordered.push(screenshot);
    }
  }
  for (const item of media) {
    if (seenPaths.has(item.path)) continue;
    seenPaths.add(item.path);
    ordered.push(item);
  }
  return ordered;
}

export function lightboxEntryTitle(media: TestReviewMedia): string {
  if (media.label) return media.label;
  return getMediaKind(media) === 'video' ? 'Video' : media.fileName;
}

export function buildLightboxItem(
  media: TestReviewMedia,
  test: TestReviewEvidence,
  title?: string,
): LightboxItem {
  const gallery = getLightboxGalleryMedia(test);
  const clickedIndex = gallery.findIndex(candidate => candidate.path === media.path);
  if (clickedIndex === -1) {
    return {
      entries: [{ media, title: title || lightboxEntryTitle(media) }],
      index: 0,
      test,
    };
  }
  return {
    entries: gallery.map((galleryMedia, index) => ({
      media: galleryMedia,
      title: index === clickedIndex && title ? title : lightboxEntryTitle(galleryMedia),
    })),
    index: clickedIndex,
    test,
  };
}
